import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, switchMap } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';

import * as ScheduleEditorActions from './schedule-editor.actions';
import { IncrementalProgressService } from 'app/admin/schedules/schedules-editor/incremental-progress.service';
import { Store } from '@ngrx/store';
import { AppState } from '../app-state/app-state';
import { loadedIncrementalProgressesAt } from './schedule-editor.selectors';

@Injectable()
export class ScheduleEditorEffects {
	loadIncrementalProgresses$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ScheduleEditorActions.loadIncrementalProgresses),
			switchMap(() =>
				this.incrementalProgressService.listProgress().pipe(
					map((progresses) => {
						return ScheduleEditorActions.loadIncrementalProgressesSuccess({ progresses });
					}),
					catchError((error: unknown) => {
						return of(ScheduleEditorActions.loadIncrementalProgressesFailure({ error }));
					})
				)
			)
		);
	});

	createIncrementalProgress$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ScheduleEditorActions.createIncrementalProgress),
			switchMap(({ correlationId }) =>
				this.incrementalProgressService.createProgress().pipe(
					map((progress) => {
						return ScheduleEditorActions.createIncrementalProgressSuccess({ correlationId, progress });
					}),
					catchError((error: unknown) => {
						return of(ScheduleEditorActions.createIncrementalProgressFailure({ correlationId, error }));
					})
				)
			)
		);
	});

	updateIncrementalProgress$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ScheduleEditorActions.updateIncrementalProgress),
			switchMap(({ correlationId, id, data }) =>
				this.incrementalProgressService.updateProgress(id, data).pipe(
					map((progress) => {
						return ScheduleEditorActions.updateIncrementalProgressSuccess({ correlationId, progress });
					}),
					catchError((error: unknown) => {
						return of(ScheduleEditorActions.updateIncrementalProgressFailure({ correlationId, error }));
					})
				)
			)
		);
	});

	private static readonly ReloadThreshold = 60_000;
	hydrateIncrementalProgresses$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ScheduleEditorActions.hydrateIncrementalProgresses),
			concatLatestFrom(() => this.store.select(loadedIncrementalProgressesAt)),
			switchMap(([_, loadedAt]) =>
				!loadedAt || loadedAt.getTime() + ScheduleEditorEffects.ReloadThreshold < new Date().getTime()
					? of(ScheduleEditorActions.loadIncrementalProgresses())
					: EMPTY
			)
		);
	});

	constructor(private actions$: Actions, private store: Store<AppState>, private incrementalProgressService: IncrementalProgressService) {}
}
